import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Filter,
  ChevronRight,
  Building2,
  Phone,
  Mail,
  Calendar as CalendarIcon,
  Loader2,
  X,
  Download,
  Upload,
  MoreVertical,
  FileSpreadsheet
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "New Lead", color: "bg-blue-900/50 text-blue-400 border-blue-800" },
  { value: "In Follow-Up", color: "bg-purple-900/50 text-purple-400 border-purple-800" },
  { value: "Warm", color: "bg-orange-900/50 text-orange-400 border-orange-800" },
  { value: "Hot", color: "bg-red-900/50 text-red-400 border-red-800" },
  { value: "Converted", color: "bg-green-900/50 text-green-400 border-green-800" },
  { value: "Lost", color: "bg-slate-800/50 text-slate-400 border-slate-700" },
];

const Prospects = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    source: "",
    status: "New Lead",
    notes: "",
  });

  useEffect(() => {
    fetchProspects();
  }, [statusFilter, search]);

  const fetchProspects = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/prospects?${params}`);
      setProspects(response.data);
    } catch (error) {
      console.error("Error fetching prospects:", error);
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        follow_up_by: followUpDate ? format(followUpDate, "yyyy-MM-dd") : null,
      };
      await axios.post(`${API}/prospects`, payload);
      toast.success("Prospect added successfully!");
      setDialogOpen(false);
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        source: "",
        status: "New Lead",
        notes: "",
      });
      setFollowUpDate(null);
      fetchProspects();
    } catch (error) {
      console.error("Error creating prospect:", error);
      toast.error("Failed to add prospect");
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/prospects/export/csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prospects_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success("Prospects exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export prospects");
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      
      const prospects_data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        prospects_data.push(row);
      }

      const response = await axios.post(`${API}/prospects/import`, prospects_data);
      
      toast.success(`Imported ${response.data.imported} prospects! (${response.data.skipped} skipped)`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      
      setImportDialogOpen(false);
      fetchProspects();
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import prospects");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((s) => s.value === status);
    return option?.color || "bg-slate-800 text-slate-400";
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Prospects</h1>
          <p className="text-slate-400">
            {prospects.length} prospect{prospects.length !== 1 ? "s" : ""} tracked
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Import/Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="csv-menu-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1E293B] border-slate-700">
              <DropdownMenuItem
                onClick={handleExportCSV}
                className="cursor-pointer text-slate-300 hover:text-white"
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setImportDialogOpen(true)}
                className="cursor-pointer text-slate-300 hover:text-white"
                data-testid="import-csv-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import from CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gba-green hover:bg-gba-green-hover text-white font-bold py-3 shadow-glow btn-press"
                data-testid="add-prospect-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Prospect
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">
                  Add New Prospect
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-[#0F172A] border-slate-700 text-white"
                    data-testid="prospect-name-input"
                  />
                </div>

                <div className="space-y-2">
                <Label className="text-slate-300">Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Inc."
                  className="bg-[#0F172A] border-slate-700 text-white"
                  data-testid="prospect-company-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="bg-[#0F172A] border-slate-700 text-white"
                    data-testid="prospect-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@acme.com"
                    className="bg-[#0F172A] border-slate-700 text-white"
                    data-testid="prospect-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Source</Label>
                  <Input
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="LinkedIn, Referral, etc."
                    className="bg-[#0F172A] border-slate-700 text-white"
                    data-testid="prospect-source-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-[#0F172A] border-slate-700 text-white" data-testid="prospect-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-700">
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Follow Up By</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-[#0F172A] border-slate-700 text-white hover:bg-slate-800"
                      data-testid="prospect-followup-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {followUpDate ? format(followUpDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#1E293B] border-slate-700" align="start">
                    <Calendar
                      mode="single"
                      selected={followUpDate}
                      onSelect={setFollowUpDate}
                      initialFocus
                      className="bg-[#1E293B]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Initial notes about this prospect..."
                  className="w-full h-24 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white placeholder:text-slate-600 focus:ring-2 focus:ring-gba-green focus:border-transparent resize-none"
                  data-testid="prospect-notes-input"
                />
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 uppercase tracking-wide shadow-glow"
                data-testid="prospect-submit-button"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Add Prospect"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company, email..."
            className="pl-10 bg-[#1E293B] border-slate-700 text-white"
            data-testid="prospect-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="status-filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || statusFilter !== "all") && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Prospects List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
        </div>
      ) : prospects.length === 0 ? (
        <Card className="bg-[#1E293B] border-slate-700 border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-white mb-2">
              {search || statusFilter ? "No Matching Prospects" : "No Prospects Yet"}
            </h3>
            <p className="text-slate-400 mb-4">
              {search || statusFilter
                ? "Try adjusting your search or filters"
                : "Add your first prospect to start tracking follow-ups"}
            </p>
            {!search && !statusFilter && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-gba-green hover:bg-gba-green-hover text-white font-bold"
              >
                <Plus className="w-4 h-4 mr-2" /> Add First Prospect
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {prospects.map((prospect, index) => (
            <Link
              key={prospect.id}
              to={`/prospects/${prospect.id}`}
              data-testid={`prospect-card-${prospect.id}`}
            >
              <Card
                className={`bg-[#1E293B] border-slate-700 card-hover animate-fade-in stagger-${Math.min(index + 1, 5)}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white truncate">
                          {prospect.name}
                        </h3>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadge(
                            prospect.status
                          )}`}
                        >
                          {prospect.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                        {prospect.company && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {prospect.company}
                          </span>
                        )}
                        {prospect.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {prospect.phone}
                          </span>
                        )}
                        {prospect.email && (
                          <span className="flex items-center gap-1 truncate max-w-[200px]">
                            <Mail className="w-4 h-4" />
                            {prospect.email}
                          </span>
                        )}
                      </div>
                      {prospect.follow_up_by && (
                        <div className="mt-2 flex items-center gap-1 text-sm">
                          <CalendarIcon className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400">
                            Follow up: {prospect.follow_up_by}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-slate-500">Techniques Used</p>
                        <p className="text-lg font-bold text-white">
                          {prospect.techniques_used?.length || 0}
                          <span className="text-slate-500 text-sm">/32</span>
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white flex items-center gap-2">
              <Upload className="w-6 h-6 text-gba-green" />
              Import Prospects
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-400 text-sm">
              Upload a CSV file with the following columns:
            </p>
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <code className="text-xs text-gba-green">
                Name, Company, Phone, Email, Source, Status, Notes, Follow Up By
              </code>
            </div>
            <p className="text-slate-500 text-xs">
              • First row should be headers
              <br />
              • Name is required for each row
              <br />
              • Duplicates (same name + email) will be skipped
            </p>

            <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center hover:border-gba-green/50 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-white font-medium">Click to upload CSV</p>
                <p className="text-slate-500 text-sm mt-1">or drag and drop</p>
              </label>
            </div>

            {importing && (
              <div className="flex items-center justify-center gap-2 text-gba-green">
                <Loader2 className="w-5 h-5 animate-spin" />
                Importing prospects...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prospects;
